import { useState, useEffect, useCallback, useMemo } from 'react';
import { useStore } from './store';
import { searchIndex } from './search';
import { SearchBar, SearchHelpTooltip } from './components/SearchBar';
import { FileLoader } from './components/FileLoader';
import { VirtualList, GroupedVirtualList } from './components/VirtualList';
import { EndpointCard } from './components/EndpointCard';
import { EndpointDetailPanel } from './components/EndpointDetailPanel';
import { ThemeToggle } from './components/ThemeToggle';
import { SpecList } from './components/SpecList';
import type { Endpoint, ViewMode, HttpMethod, SearchMatch } from './types';
import './index.css';

const ITEM_HEIGHT = 72; // Height of compact endpoint card
const HEADER_HEIGHT = 48; // Height of group header

export function App() {
  const [results, setResults] = useState<Endpoint[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('search');
  
  const {
    specs,
    endpoints,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    clearFilters,
    selectedEndpoint,
    setSelectedEndpoint,
    setActiveMatches,
    expandedGroups,
    toggleGroup,
  } = useStore();

  // Initialize search index when endpoints change
  useEffect(() => {
    searchIndex.buildIndex(endpoints);
    setResults(endpoints);
  }, [endpoints]);

  // Handle search results with matches for highlighting
  const handleSearchResults = useCallback((searchResults: { endpoint: Endpoint; matches: SearchMatch[] }[]) => {
    const endpoints = searchResults.map(r => r.endpoint);
    setResults(endpoints);
    
    // Store matches for highlighting
    const matchesMap = new Map<string, SearchMatch[]>();
    for (const result of searchResults) {
      if (result.matches.length > 0) {
        matchesMap.set(result.endpoint.id, result.matches);
      }
    }
    setActiveMatches(matchesMap);
  }, [setActiveMatches]);

  // Group endpoints for explorer view
  const groupedEndpoints = useMemo(() => {
    const groups = new Map<string, Endpoint[]>();
    
    for (const endpoint of results) {
      const key = endpoint.tags[0] || 'Untagged';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(endpoint);
    }
    
    return Array.from(groups.entries())
      .map(([title, items]) => ({ id: title, title, items }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [results]);

  // Filter change handler
  const handleFiltersChange = useCallback((newFilters: { methods?: HttpMethod[]; specs?: string[]; tags?: string[] }) => {
    setFilters(newFilters);
  }, [setFilters]);

  const totalCount = endpoints.length;
  const resultCount = results.length;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            OpenAPI GPS
          </h1>
          <span className="endpoint-count">
            {resultCount.toLocaleString()}
            {resultCount !== totalCount && ` / ${totalCount.toLocaleString()}`}
            {' '}endpoints
          </span>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            <button
              className={viewMode === 'search' ? 'active' : ''}
              onClick={() => setViewMode('search')}
              title="Search view"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Search
            </button>
            <button
              className={viewMode === 'explorer' ? 'active' : ''}
              onClick={() => setViewMode('explorer')}
              title="Explorer view"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3h7v7H3z" />
                <path d="M14 3h7v7h-7z" />
                <path d="M14 14h7v7h-7z" />
                <path d="M3 14h7v7H3z" />
              </svg>
              Explorer
            </button>
          </div>
          <SearchHelpTooltip />
          <ThemeToggle />
        </div>
      </header>

      <main className="app-main">
        <aside className="app-sidebar">
          <FileLoader />
          <SpecList />
          
          {specs.length > 0 && (
            <div className="keyboard-shortcuts">
              <h4>Shortcuts</h4>
              <div className="shortcut">
                <kbd>/</kbd> Focus search
              </div>
              <div className="shortcut">
                <kbd>Esc</kbd> Clear/close
              </div>
              <div className="shortcut">
                <kbd>↑↓</kbd> Navigate
              </div>
              <div className="shortcut">
                <kbd>Enter</kbd> Select
              </div>
            </div>
          )}
        </aside>

        <div className={`app-content ${selectedEndpoint ? 'with-detail' : ''}`}>
          {specs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <h2>No OpenAPI specs loaded</h2>
              <p>Drop a spec file or load from URL to get started</p>
            </div>
          ) : (
            <>
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onResults={handleSearchResults}
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />

              {viewMode === 'search' ? (
                <div className="results-container">
                  {results.length === 0 ? (
                    <div className="no-results">
                      <p>No endpoints found</p>
                      {searchQuery && (
                        <button onClick={() => { setSearchQuery(''); clearFilters(); }}>
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <VirtualList
                      items={results}
                      itemHeight={ITEM_HEIGHT}
                      renderItem={(endpoint) => (
                        <EndpointCard
                          endpoint={endpoint}
                          compact
                          isSelected={selectedEndpoint?.id === endpoint.id}
                          onSelect={setSelectedEndpoint}
                        />
                      )}
                    />
                  )}
                </div>
              ) : (
                <div className="results-container">
                  {groupedEndpoints.length === 0 ? (
                    <div className="no-results">
                      <p>No endpoints found</p>
                    </div>
                  ) : (
                    <GroupedVirtualList
                      groups={groupedEndpoints}
                      itemHeight={ITEM_HEIGHT}
                      headerHeight={HEADER_HEIGHT}
                      expandedGroups={expandedGroups}
                      renderGroupHeader={(group, isExpanded) => (
                        <button
                          className="group-header"
                          onClick={() => toggleGroup(group.id)}
                        >
                          <span className="group-toggle">{isExpanded ? '▼' : '▶'}</span>
                          <span className="group-title">{group.title}</span>
                          <span className="group-count">{group.items.length}</span>
                        </button>
                      )}
                      renderItem={(endpoint) => (
                        <EndpointCard
                          endpoint={endpoint}
                          compact
                          isSelected={selectedEndpoint?.id === endpoint.id}
                          onSelect={setSelectedEndpoint}
                        />
                      )}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Split-pane detail panel instead of modal */}
        <EndpointDetailPanel />
      </main>
    </div>
  );
}

export default App;
